// Phase 0091 — Certification Expiry Report page.
// Lists certifications expiring in next N days (default 60), with the
// action recommended by the backend service.

import { useState } from 'react'
import {
  App, Alert, Button, Card, Empty, Input, Select, Space, Table, Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useMutation, useQuery } from '@tanstack/react-query'

import {
  hrAutomationApi, type CertExpiryRow,
} from '@/features/hr/schedulerApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

const ACTION_COLOR: Record<string, string> = {
  'Expired — block deployment': 'red',
  'Schedule refresher': 'volcano',
  'Plan refresher': 'gold',
  'Monitor': 'blue',
}

const STATUS_COLOR: Record<string, string> = {
  CERTIFIED: 'green',
  IN_PROGRESS: 'blue',
  EXPIRED: 'red',
  NOT_CERTIFIED: 'default',
}

export default function CertificationExpiryPage() {
  const { message: msg } = App.useApp()
  const [withinDays, setWithinDays] = useState(60)
  const [search, setSearch] = useState('')

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['cert-expiry-report', withinDays],
    queryFn: () => hrAutomationApi.certExpiryReport(withinDays),
  })

  const checkMut = useMutation({
    mutationFn: () => hrAutomationApi.checkCertExpiry(),
    onSuccess: (data: unknown) => {
      const d = data as { alerts_created: number; newly_expired: number }
      msg.success(`Created ${d.alerts_created} alerts. ${d.newly_expired} newly expired.`)
      refetch()
    },
    onError: (e: Error) => msg.error(e.message),
  })

  // Filtered list
  const filtered = search
    ? rows.filter((r) => {
        const q = search.toLowerCase()
        return (
          r.emp_name.toLowerCase().includes(q) ||
          r.emp_code.toLowerCase().includes(q) ||
          r.skill_name.toLowerCase().includes(q)
        )
      })
    : rows

  // Quick rollup stats
  const stats = {
    expired: rows.filter((r) => r.days_to_expiry <= 0).length,
    in7: rows.filter((r) => r.days_to_expiry > 0 && r.days_to_expiry <= 7).length,
    in30: rows.filter((r) => r.days_to_expiry > 7 && r.days_to_expiry <= 30).length,
    in60: rows.filter((r) => r.days_to_expiry > 30).length,
  }

  const columns: ColumnsType<CertExpiryRow> = [
    {
      title: 'Employee',
      key: 'emp',
      width: 220,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.emp_name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.emp_code}</Text>
        </Space>
      ),
    },
    {
      title: 'Skill',
      key: 'skill',
      width: 280,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{r.skill_name}</Text>
          <Space size={4}>
            {r.skill_category && <Tag>{r.skill_category}</Tag>}
            {r.machine_type && <Tag color="purple">{r.machine_type}</Tag>}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (s: string) => <Tag color={STATUS_COLOR[s] ?? 'default'}>{s}</Tag>,
      filters: [
        { text: 'CERTIFIED', value: 'CERTIFIED' },
        { text: 'EXPIRED', value: 'EXPIRED' },
        { text: 'NOT_CERTIFIED', value: 'NOT_CERTIFIED' },
        { text: 'IN_PROGRESS', value: 'IN_PROGRESS' },
      ],
      onFilter: (v, r) => r.status === v,
    },
    {
      title: 'Certified till',
      dataIndex: 'certified_till',
      width: 130,
      render: (d: string) => dayjs(d).format('MMM D, YYYY'),
      sorter: (a, b) => dayjs(a.certified_till).valueOf() - dayjs(b.certified_till).valueOf(),
    },
    {
      title: 'Days to expiry',
      dataIndex: 'days_to_expiry',
      width: 130,
      render: (d: number) => {
        const color = d <= 0 ? '#cf1322' : d <= 7 ? '#fa8c16' : d <= 30 ? '#faad14' : '#52c41a'
        return <Text strong style={{ color, fontSize: 16 }}>{d}</Text>
      },
      sorter: (a, b) => a.days_to_expiry - b.days_to_expiry,
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Action required',
      dataIndex: 'action_required',
      width: 220,
      render: (a: string) => <Tag color={ACTION_COLOR[a] ?? 'default'}>{a}</Tag>,
    },
  ]

  return (
    <>
      <PageHeader
        title="Certification Expiry Report"
        subtitle="Skills expiring soon and the action HR/HoD should take. The scheduler emits alerts at 30/15/7/0 day boundaries automatically."
        extra={
          <Space>
            <Button onClick={() => refetch()}>Refresh</Button>
            <Button type="primary" onClick={() => checkMut.mutate()}
                    loading={checkMut.isPending}>
              Run expiry check now
            </Button>
          </Space>
        }
      />

      {/* Stats strip */}
      <Space style={{ marginBottom: 16, width: '100%' }} wrap>
        <Card size="small" style={{ minWidth: 140 }}>
          <Space direction="vertical" size={0}>
            <Text type="secondary">Expired</Text>
            <Text strong style={{ fontSize: 20, color: '#cf1322' }}>{stats.expired}</Text>
          </Space>
        </Card>
        <Card size="small" style={{ minWidth: 140 }}>
          <Space direction="vertical" size={0}>
            <Text type="secondary">Expiring ≤ 7 days</Text>
            <Text strong style={{ fontSize: 20, color: '#fa8c16' }}>{stats.in7}</Text>
          </Space>
        </Card>
        <Card size="small" style={{ minWidth: 140 }}>
          <Space direction="vertical" size={0}>
            <Text type="secondary">Expiring ≤ 30 days</Text>
            <Text strong style={{ fontSize: 20, color: '#faad14' }}>{stats.in30}</Text>
          </Space>
        </Card>
        <Card size="small" style={{ minWidth: 140 }}>
          <Space direction="vertical" size={0}>
            <Text type="secondary">Expiring 31-{withinDays}d</Text>
            <Text strong style={{ fontSize: 20, color: '#52c41a' }}>{stats.in60}</Text>
          </Space>
        </Card>
      </Space>

      {/* Filters */}
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          value={withinDays}
          onChange={setWithinDays}
          style={{ width: 180 }}
          options={[
            { value: 30, label: 'Within 30 days' },
            { value: 60, label: 'Within 60 days' },
            { value: 90, label: 'Within 90 days' },
            { value: 180, label: 'Within 180 days' },
            { value: 365, label: 'Within 1 year' },
          ]}
        />
        <Input.Search
          placeholder="Search employee, skill"
          allowClear
          style={{ width: 280 }}
          onSearch={setSearch}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Text type="secondary">{filtered.length} of {rows.length} certifications</Text>
      </Space>

      {stats.expired > 0 && (
        <Alert
          type="error" showIcon style={{ marginBottom: 12 }}
          message={`${stats.expired} certification(s) already expired`}
          description="Workers with EXPIRED status are blocked from critical-post deployment unless an override reason is recorded."
        />
      )}

      {filtered.length === 0 ? (
        <Empty description={
          isLoading ? 'Loading…' : `No certifications expiring in the next ${withinDays} days.`
        } />
      ) : (
        <Table<CertExpiryRow>
          rowKey="skill_matrix_id"
          columns={columns}
          dataSource={filtered}
          loading={isLoading}
          pagination={{ pageSize: 30, showSizeChanger: true }}
          size="middle"
        />
      )}
    </>
  )
}
